// Asset loading. PNGs are converted to 8-bit indexed surfaces using idx =
// round((r+g+b)/3): this reproduces how SDL mapped each sprite colour onto the
// screen's greyscale *logical* palette before the dynamic physical palette was
// applied. Transparent pixels come from alpha and/or the per-file colour key.

import { Surface, loadFontData } from './surface';

const BASE = './assets/';

// Custom HD filenames (default: dat/i/foo.png → dat/i/v2/foo.png).
const HD_ALIASES: Record<string, string> = {
  'dat/i/player.png': 'dat/i/v2/player_hd.png',
  'dat/i/tileset.png': 'dat/i/v2/tileset_crystal.png',
};

function hdPathFor(datPath: string): string {
  return HD_ALIASES[datPath] ?? `dat/i/v2/${datPath.replace(/^dat\/i\//, '')}`;
}

/** Native pixel scale of loaded asset relative to original 1x art (2 = HD). */
export const ASSET_SCALE: Record<string, number> = {};

export function getAssetScale(datPath: string): number {
  return ASSET_SCALE[datPath] ?? 1;
}

function resolve(datPath: string): string {
  // Original code uses "dat/..." paths; assets live under public/assets/.
  return BASE + datPath.replace(/^dat\//, '');
}

// Files that need a colour key (transparent index). Others are fully opaque.
const COLOR_KEYS: Record<string, number> = {
  'dat/i/player.png': 0,
  'dat/i/meter.png': 0,
  'dat/i/mons1.png': 255,
  'dat/i/mons2.png': 255,
  'dat/i/mons3.png': 255,
  'dat/i/mons4.png': 255,
  'dat/i/mons5.png': 255,
  'dat/i/mons6.png': 255,
  'dat/i/mons7.png': 255,
  'dat/i/mons8.png': 255,
  'dat/i/mons9.png': 255,
  'dat/i/mons10.png': 255,
  'dat/i/reticle.png': 255,
  'dat/i/inrange.png': 255,
  'dat/i/hidden_monster.png': 255,
  'dat/i/teleflash.png': 0,
  'dat/i/gem.png': 255,
  'dat/i/star1.png': 255,
  'dat/i/star2.png': 255,
  'dat/i/star3.png': 255,
  'dat/i/artifacts.png': 0,
  'dat/i/agate.png': 0,
  'dat/i/boss1.png': 255,
  'dat/i/boss2.png': 255,
  'dat/i/boss3.png': 0,
  'dat/i/boss4.png': 0,
  'dat/i/stream.png': 0,
  'dat/i/glitter.png': 0,
};

const IMAGE_LIST = [
  'dat/i/icon.png',
  'dat/i/title.png',
  'dat/i/tileset.png',
  'dat/i/player.png',
  'dat/i/meter.png',
  'dat/i/circuit-bar.png',
  'dat/i/artifacts.png',
  'dat/i/agate.png',
  'dat/i/automap.png',
  'dat/i/overview.png',
  'dat/i/reticle.png',
  'dat/i/inrange.png',
  'dat/i/hidden_monster.png',
  'dat/i/teleflash.png',
  'dat/i/gem.png',
  'dat/i/star1.png',
  'dat/i/star2.png',
  'dat/i/star3.png',
  'dat/i/mons1.png',
  'dat/i/mons2.png',
  'dat/i/mons3.png',
  'dat/i/mons4.png',
  'dat/i/mons5.png',
  'dat/i/mons6.png',
  'dat/i/mons7.png',
  'dat/i/mons8.png',
  'dat/i/mons9.png',
  'dat/i/mons10.png',
  'dat/i/boss1.png',
  'dat/i/boss2.png',
  'dat/i/boss3.png',
  'dat/i/boss4.png',
  'dat/i/boss_icon.png',
  'dat/i/circuits_1.png',
  'dat/i/stream.png',
  'dat/i/glitter.png',
  'dat/i/fin.png',
  'dat/i/theend.png',
  'dat/i/true_end.png',
  'dat/i/wuss_ending.png',
];

// HD asset path overrides (stored under original dat path keys).
const ASSET_PATHS: Record<string, string> = Object.fromEntries(
  IMAGE_LIST.map((p) => [p, hdPathFor(p)])
);

const LOC_LIST = [
  'dat/d/centre.loc',
  'dat/d/weapon.loc',
  'dat/d/bossroom.loc',
  'dat/d/fbossroom.loc',
  'dat/d/cstream.loc',
];

const imgs: Record<string, Surface> = {};
const surfacePaths = new WeakMap<Surface, string>();
const surfaceScale = new WeakMap<Surface, number>();
const locs: Record<string, Uint8Array> = {};
export let helpText = '';

export function getSurfaceScale(s: Surface): number {
  return surfaceScale.get(s) ?? 1;
}

/** Convert asset pixel size to game-coordinate pixels. */
export function toGamePx(assetPx: number, nativeScale: number): number {
  return assetPx / nativeScale;
}

/** Source rect size for a 1x-layout cell at the given native scale. */
export function baseSrcRect(baseW: number, baseH: number, nativeScale: number): { w: number; h: number } {
  return { w: baseW * nativeScale, h: baseH * nativeScale };
}

export function getSurfacePath(surf: Surface): string | undefined {
  return surfacePaths.get(surf);
}

export function getImg(datPath: string): Surface {
  const s = imgs[datPath];
  if (!s) throw new Error('Missing image: ' + datPath);
  return s;
}

export function getLoc(datPath: string): Uint8Array {
  return locs[datPath];
}

async function loadImageSurfaceUrl(loadPath: string, colorKeyPath: string): Promise<Surface> {
  const url = resolve(loadPath);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} loading ${url}`);
  const blob = await res.blob();
  const img = new Image();
  img.src = URL.createObjectURL(blob);
  try {
    await img.decode();
  } finally {
    URL.revokeObjectURL(img.src);
  }
  if (img.naturalWidth < 1 || img.naturalHeight < 1) {
    throw new Error(`Invalid image dimensions: ${url}`);
  }
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0);
  const data = ctx.getImageData(0, 0, w, h).data;

  const ck = colorKeyPath in COLOR_KEYS ? COLOR_KEYS[colorKeyPath] : -1;
  const alpha = new Uint8Array(w * h);
  const surf = new Surface(w, h, ck, alpha);
  const pix = surf.pix;
  let hasTransparent = false;
  for (let i = 0, p = 0; i < w * h; i++, p += 4) {
    const idx = Math.round((data[p] + data[p + 1] + data[p + 2]) / 3);
    pix[i] = idx;
    if (data[p + 3] < 128) {
      alpha[i] = 0;
      hasTransparent = true;
    } else {
      alpha[i] = 1;
    }
  }
  if (!hasTransparent) surf.alpha = null;
  return surf;
}

async function loadImageSurface(datPath: string): Promise<Surface> {
  let nativeScale = 1;
  let surf: Surface;
  const hdPath = ASSET_PATHS[datPath];
  if (hdPath) {
    try {
      surf = await loadImageSurfaceUrl(hdPath, datPath);
      nativeScale = 2;
      ASSET_SCALE[datPath] = 2;
    } catch (e) {
      console.warn(`HD asset missing for ${datPath}, using 1x fallback`, e);
      ASSET_SCALE[datPath] = 1;
      surf = await loadImageSurfaceUrl(datPath, datPath);
    }
  } else {
    surf = await loadImageSurfaceUrl(datPath, datPath);
  }
  surfaceScale.set(surf, nativeScale);
  return surf;
}

async function loadBytes(url: string): Promise<Uint8Array> {
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  return new Uint8Array(buf);
}

// Build the darkened "fog of war" tileset (levelblit.c: *pp = *pp/2 + 128).
export function makeFog(tileset: Surface): Surface {
  const fog = new Surface(tileset.w, tileset.h, tileset.colorKey, null);
  for (let i = 0; i < tileset.pix.length; i++) {
    fog.pix[i] = (Math.trunc(tileset.pix[i] / 2) + 128) & 255;
  }
  return fog;
}

export async function loadAllAssets(onProgress?: (p: number) => void): Promise<void> {
  const total = IMAGE_LIST.length + LOC_LIST.length + 2;
  let done = 0;
  const tick = () => {
    done++;
    if (onProgress) onProgress(done / total);
  };

  // Font
  const fontBytes = await loadBytes(resolve('dat/d/font.dat'));
  loadFontData(fontBytes);
  tick();

  // Help file
  helpText = await (await fetch(resolve('dat/d/helpfile.txt'))).text();
  tick();

  // Images (in parallel, but report progress as they complete)
  await Promise.all(
    IMAGE_LIST.map(async (p) => {
      imgs[p] = await loadImageSurface(p);
      surfacePaths.set(imgs[p], p);
      tick();
    })
  );

  // .loc templates
  await Promise.all(
    LOC_LIST.map(async (p) => {
      locs[p] = await loadBytes(resolve(p));
      tick();
    })
  );
}
